import { t } from 'ttag';

import { getCurrentSignFolder } from '../components/filters/utils';
import { flattenObjectsArray } from '../lib/helpers';

import { SIGN_FOLDERS } from '../components/filters/constants';
import { PermissionCategory } from '../components/proRateInfoPopup/proRateInfoPopupTypes';
import { getLocationQuery } from '../lib/url';
import { DocumentFolder } from './enum';

const DEFAULT_ADDTITIONAL_QUERY_PARAMETERS = {
    wait_my_sign: undefined,
    review_folder: undefined,
    has_comments: undefined,
};

const mainLinks = () => [
    {
        id: 'external',
        name: t`Зовнішні документи`,
        path: '/app/documents',
        query: {
            folder_id: DocumentFolder.NOT_INTERNAL.value,
            ...DEFAULT_ADDTITIONAL_QUERY_PARAMETERS,
        },
        child: [
            {
                id: 'incoming',
                name: t`Вхідні`,
                path: '/app/documents',
                query: {
                    folder_id: DocumentFolder.INBOX.value,
                    ...DEFAULT_ADDTITIONAL_QUERY_PARAMETERS,
                },
            },
            {
                id: 'outgoing',
                name: t`Вихідні`,
                path: '/app/documents',
                query: {
                    folder_id: DocumentFolder.OUTGOING.value,
                    ...DEFAULT_ADDTITIONAL_QUERY_PARAMETERS,
                },
            },
        ],
    },
    {
        id: 'internal',
        name: t`Внутрішні документи`,
        path: '/app/documents',
        query: {
            folder_id: DocumentFolder.INTERNAL.value,
            ...DEFAULT_ADDTITIONAL_QUERY_PARAMETERS,
        },
        permission: PermissionCategory.REVIEWS,
    },
];

const MAIN_MENU_STRUCTURE = [
    {
        id: 'wait_my_sign',
        name: t`Очікують на мій підпис`,
        path: '/app/documents',
        query: {
            folder_id: [
                DocumentFolder.NOT_INTERNAL.value,
                DocumentFolder.INTERNAL.value,
            ],
            wait_my_sign: true,
            review_folder: undefined,
            has_comments: undefined,
            status_id: undefined,
            conditions2: undefined,
        },
        hint: t`Документи, в котрих ви позначені як підписант`,
    },
    {
        id: 'review_folder',
        name: t`Очікують на моє погодження`,
        path: '/app/documents',
        query: {
            review_folder: 'wait_my_review',
            wait_my_sign: undefined,
            has_comments: undefined,
            status_id: undefined,
            conditions2: undefined,
        },
    },
    {
        id: 'has_comments',
        name: t`З коментарями`,
        path: '/app/documents',
        query: {
            has_comments: true,
            review_folder: undefined,
            wait_my_sign: undefined,
            status_id: undefined,
            conditions2: undefined,
        },
    },
];

const invoicesMenuStructure = [
    {
        id: 'all_invoices',
        name: t`Всі документи`,
        path: '/app/tax-invoices',
        query: {
            folder_id: undefined,
            status_id: undefined,
            is_one_sign: undefined,
        },
    },
    {
        id: 'invoices_downloaded',
        name: 'Завантажені',
        path: '/app/tax-invoices',
        query: {
            folder_id: undefined,
            status_id: undefined,
            is_one_sign: undefined,
        },
    },
    {
        id: 'invoices_sent',
        name: 'Надіслані на реєстрацію в ДФС',
        path: '/app/tax-invoices',
        query: {
            folder_id: undefined,
            status_id: undefined,
            is_one_sign: undefined,
        },
    },
    {
        id: 'invoices_recieved',
        name: 'Отримані ДФС',
        path: '/app/tax-invoices',
        query: {
            folder_id: undefined,
            status_id: undefined,
            is_one_sign: undefined,
        },
    },
    {
        id: 'invoices_accepted',
        name: 'Прийняті на розгляд ДФС',
        path: '/app/tax-invoices',
        query: {
            folder_id: undefined,
            status_id: undefined,
            is_one_sign: undefined,
        },
    },
    {
        id: 'invoices_registered',
        name: 'Успішно зареєстровані',
        path: '/app/tax-invoices',
        query: {
            folder_id: undefined,
            status_id: undefined,
            is_one_sign: undefined,
        },
    },
    {
        id: 'invoices_rejected',
        name: 'Відхилені ДФС',
        path: '/app/tax-invoices',
        query: {
            folder_id: undefined,
            status_id: undefined,
            is_one_sign: undefined,
        },
    },
];

const adminMenuStructure = () => [
    {
        id: 'settings',
        name: t`Налаштування облікового запису`,
        locationId: '0.0',
        path: '/app/settings',
    },
    {
        id: 'companies',
        name: t`Мої компанії`,
        locationId: '0.1',
        path: '/app/settings/companies',
    },
    {
        id: 'document-settings',
        name: t`Документи`,
        path: '/app/settings/document-settings',
        accesses: [
            'isAdmin',
            'canEditDocumentTemplates',
            'canEditDocumentFields',
        ],
    },
    {
        id: 'contacts',
        name: t`Мої контакти`,
        path: '/app/settings/contacts',
    },
    {
        id: 'ttn-settings',
        name: t`ТТН`,
        path: '/app/settings/ttn',
    },
    {
        id: 'statistics',
        name: t`Статистика`,
        locationId: '0.3',
        path: '/app/settings/statistics',
        access: 'isSuperAdmin',
    },
    {
        id: 'special-abilities',
        name: t`Спеціальні можливості`,
        locationId: '0.2',
        path: '/app/settings/special-abilities',
        access: 'isSuperAdmin',
    },
    {
        id: 'banner-settings',
        name: t`Налаштування банера`,
        path: '/app/settings/banner',
        access: 'isSuperAdmin',
    },
];

function getFlattenMainMenuStructure() {
    return flattenObjectsArray([...mainLinks(), MAIN_MENU_STRUCTURE], 'child');
}

function getCurrentMainFolder(query) {
    return flattenObjectsArray(mainLinks(), 'child').find(
        (link) => String(link.query.folder_id) === String(query.folder_id),
    );
}

function getCurrentAdditionalParams(query) {
    return MAIN_MENU_STRUCTURE.find((link) =>
        Object.keys(query).find((key) => key === link.id),
    );
}

function getLocationId(location) {
    let locationId = null;
    if (
        location.pathname === '/app/documents' ||
        location.pathname === '/app/documents/'
    ) {
        const foundSignFolder = getCurrentSignFolder(
            getLocationQuery(location),
        );
        if (foundSignFolder) locationId = foundSignFolder;
    } else if (location.pathname.indexOf('/app/doc/') >= 0) {
        locationId = 'document_page';
    } else {
        locationId = location.pathname;
    }

    return locationId;
}

function getLocationName(location) {
    const query = getLocationQuery(location);
    const foundSignFolder = getCurrentSignFolder(query);
    const foundSubFolder = getCurrentAdditionalParams(query);
    const foundMainFolder = getCurrentMainFolder(query);
    let locationName = t`Документи`;

    if (foundSignFolder) {
        locationName = SIGN_FOLDERS.find(
            (folder) => folder.value === foundSignFolder,
        ).label;
    } else if (foundSubFolder) {
        locationName = foundSubFolder.name;
    } else if (foundMainFolder) {
        locationName = foundMainFolder.name;
    }

    return locationName;
}

function isAdminPage(location) {
    return location.pathname.indexOf('/app/settings');
}

function isDocumentsPage(location) {
    return location.pathname.includes('/app/documents');
}

export {
    mainLinks,
    MAIN_MENU_STRUCTURE,
    getLocationId,
    getLocationName,
    adminMenuStructure,
    invoicesMenuStructure,
    isAdminPage,
    isDocumentsPage,
    getFlattenMainMenuStructure,
};
